import React, { useState, useEffect, useContext } from 'react';
import { string, bool, shape, object } from 'prop-types';
import { ExperienceContext, useMVCSID, useConfigService } from '@thd-nucleus/experience-context';
import { Carousel } from '@thd-olt-component-react/carousel';
import { useHelmet, LinkTag } from '@thd-nucleus/thd-helmet';
import classNames from 'classnames/bind';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import {
  initDefaultBannerImpressionWatchers,
  initImpressionWatchers
} from '../../utils/initImpressionWatchers';
import {
  getSchema,
  getSkus,
  getDefaultBanner,
  triggerNewRelic,
  parseIfJson,
  isBrandFpn
} from '../../utils/helpers';

import styles from './SponsoredBanner.module.scss';
import { newRelicConstants } from '../../constants';

let cx = classNames.bind(styles);
const hoistedImgFunction = ({ data }) => {
  const { isBrowse, avoidPreload, defaultBannerInfo } = data;
  if (isBrowse && !avoidPreload) {
    return [
      new LinkTag({
        rel: 'preload',
        as: 'image',
        href: defaultBannerInfo.imgSrc,
        id: 'spBannerPreloadLink',
      }),
    ];
  }
  return null;
};

const SponsoredBanner = (props) => {
  let {
    avoidPreload,
    pageContext,
    schema,
    slugId,
    utilizeRvData,
    showSponsoredBanner,
    externalRevJetConfig,
    enableForChapters
  } = props;
  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [hasData, setHasData] = useState(false);
  const experienceContext = useContext(ExperienceContext);
  const sponsoredContentDefaultBanner = useConfigService('fs-prop:sponsored-default-banners');
  let externalDefaultBanner = {};

  if (sponsoredContentDefaultBanner) {
    try {
      externalDefaultBanner = JSON.parse(sponsoredContentDefaultBanner);
    // eslint-disable-next-line no-empty
    } catch (error) {
    }
  }
  const defaultBannerInfo = getDefaultBanner(experienceContext, externalDefaultBanner);
  const [isBranded, setIsBranded] = useState(isBrandFpn({ experienceContext, pageContext }));
  schema = getSchema({ schema, pageContext });
  const schemaId = schema + '__banner';
  const browserId = useMVCSID();
  const data = {
    avoidPreload,
    channel: experienceContext.channel,
    isBrowse: pageContext?.isBrowse,
    defaultBannerInfo,
  };

  const onRevjetMessage = (evt) => {
    try {
      const messageData = parseIfJson(evt.data);
      if (
        messageData.module === 'REVJET_Creative'
        && messageData.method === 'dispatchEvent'
        && messageData.properties?.eventObject?.type === 'click'
      ) {
        triggerNewRelic(newRelicConstants.STANDARD_BANNER, newRelicConstants.CLICK);
      }
    } catch (event) {
      throw new Error('Failed to send click to New Relic');
    }
  };

  useHelmet('preloadBanner', { data }, hoistedImgFunction);

  useEffect(() => {
    const startTime = new Date().getTime();
    // For browse, search, category and event page schemas
    if (pageContext?.label === 'browse-search') {
      const isValidScenario = shouldMakeApiRequest({
        lastRequestScenario,
        experienceContext,
        pageContext,
        schema,
        browserId
      });

      if (isValidScenario || enableForChapters) {
        setLastRequestScenario({
          url: window.location.pathname + window.location.search,
          skus: getSkus(pageContext),
        });
        makeApiRequest({
          browserId,
          schema,
          schemaId,
          experienceContext,
          pageContext,
          setHasData,
          utilizeRvData,
          startTime,
          slugId,
          externalRevJetConfig,
          ignoreStoreId: true
        });
      }
    } else if (showSponsoredBanner) {
      makeApiRequest({
        browserId,
        schema,
        schemaId,
        experienceContext,
        pageContext,
        setHasData,
        utilizeRvData,
        startTime,
        slugId,
        externalRevJetConfig,
        ignoreStoreId: true
      });
    }
  }, [pageContext?.data, slugId, schema, browserId]);

  useEffect(() => {
    window.addEventListener('message', onRevjetMessage, false);
    initDefaultBannerImpressionWatchers();

    return () => {
      window.removeEventListener('message', onRevjetMessage);
    };
  }, []);

  useEffect(() => {
    initImpressionWatchers(['NUCLEUS_BANNER']);
    const slideEl = document.querySelector('.carousel .slide');
    if (slideEl) {
      slideEl.style.background = '#f5f5f5';
    }
  }, [hasData]);

  if (isBranded) {
    return null;
  }

  return (
    <div
      className={cx('sponsored_banner_container')}
      id="sponsored-standard-banner-nucleus"
      data-component="SponsoredBanner"
    >
      {!hasData && (
        <div
          id="default_banner_carousel"
          style={defaultBannerInfo.spacingCss}
          data-testid="default-standard-banner"
        >
          <a href={defaultBannerInfo.href}>
            {/* eslint-disable-next-line */}
            <img
              className={cx('sponsored_banner_image')}
              alt="sponsored banner"
              src={defaultBannerInfo.imgSrc}
            />
          </a>
        </div>
      )}

      {hasData && (
        <Carousel multiItem={false} totalItems={2}>
          <div
            id="default_banner_carousel"
            style={defaultBannerInfo.spacingCss}
            data-testid="default-standard-banner"
          >
            <a href={defaultBannerInfo.href}>
              {/* eslint-disable-next-line */}
              <img
                className={cx('sponsored_banner_image')}
                alt="sponsored banner"
                src={defaultBannerInfo.imgSrc}
              />
            </a>
          </div>
          <div
            id={schemaId}
            className="nucleus-banner"
            data-usenewimpressionlogic="true"
            data-testid="sponsored-standard-banner"
          />
        </Carousel>
      )}
    </div>
  );
};

SponsoredBanner.propTypes = {
  schema: string,
  slugId: string,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
  }),
  avoidPreload: bool,
  utilizeRvData: bool,
  showSponsoredBanner: bool,
  externalRevJetConfig: object,
  enableForChapters: bool
};

SponsoredBanner.defaultProps = {
  avoidPreload: true,
  pageContext: {},
  schema: '',
  slugId: '',
  utilizeRvData: false,
  showSponsoredBanner: true,
  externalRevJetConfig: null,
  enableForChapters: false
};

export { SponsoredBanner };
